import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "./TableTemplates.module.css";
import { useNavigate } from "react-router-dom";

import Pagination from "../../../components/DynamicTable/Pagination/Pagination";
import FilterModal from "../../../components/DynamicTable/Modals/FilterModal/FilterModal";
import EditRowModal from "../../../components/DynamicTable/Modals/EditRowModal/EditRowModal";
import ManageColumnsVisibilityModal
    from "../../../components/DynamicTable/Modals/ManageColumnsVisibilityModal/ManageColumnsVisibilityModal";
import HelpModal from "../../../components/Modals/HelpModal/HelpModal";

import { useIconPack } from "../../../context/IconPackContext";
import { useLanguage } from "../../../context/LanguageContext";
import { ToastService } from "../../../service/toast.service";
import { useTableData } from "../../../hooks/table/useTableData";
import { useColumnVisibility } from "../../../hooks/table/useColumnVisibility";
import { usePendingRows } from "../../../hooks/table/usePendingRows";
import { useTableClipboard } from "../../../hooks/table/useTableClipboard";
import { useTableSelection } from "../../../hooks/table/useTableSelection";
import { useTableHotkeys } from "../../../hooks/table/useTableHotkeys";
import { useEffect, useState } from "react";
import { TableRow, TableTemplateProps } from "../../../types/types.table";
import { useInlineRowEdit } from "../../../hooks/table/useInlineRowEdit";
import { getField } from "./tableFieldRenderer";
import { convertArrayToObject, convertObjectToArray } from "../../../utils/filterAdapter";
import DeleteConfirmModal from "../../../components/DynamicTable/Modals/DeleteConfirmModal/DeleteConfirmModal";
import { useModuleAccess } from "../../../hooks/useModuleAccess";
import {getUser, postUser} from "../../../helper/http.ts";

export default function TableTemplate(props: TableTemplateProps) {
    const { t } = useLanguage();
    const { getIcon } = useIconPack();
    const navigate = useNavigate();

    const isReportMode = !!props.isReportMode;

    const { canRead, canCreate, canEdit, canDelete } = useModuleAccess(props.module?.id, isReportMode);

    const table = useTableData({
        loadData: props.loadData,
        externalFilters: props.externalFilters,
    });

    const columns = useColumnVisibility(props.fields);

    const pending = usePendingRows({
        fields: props.fields,
        moduleId: props.module?.id,
        autoSaveEnabled: props.autoSaveEnabled,
        validateRow: props.validateRow,
        createData: props.createData,
        autoFillFields: props.autoFillFields,
        loadData: table.loadData,
        t,
    });

    const selection = useTableSelection({
        data: table.data,
        pendingRows: pending.pendingRows,
        visibleColumns: columns.visibleColumns,
    });

    const clipboard = useTableClipboard({
        visibleColumns: columns.visibleColumns,
        pendingRows: pending.pendingRows,
        setPendingRows: pending.setPendingRows,
        addNewRow: pending.addNewRow,
    });

    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [rowToEdit, setRowToEdit] = useState<TableRow | null>(null);
    const [isManageColumnsOpen, setIsManageColumnsOpen] = useState(false);
    const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [rowToDelete, setRowToDelete] = useState<TableRow | null>(null);
    const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
    const [options, setOptions] = useState<Record<any, any[]>>({});
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
    const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null);
    const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
    const [sourcesStatus, setSourcesStatus] = useState<Record<string, boolean>>({});

    const [hoveredError, setHoveredError] = useState<{
        rowId: string | number;
        field: string;
        type: "edit" | "pending";
    } | null>(null);

    const inline = useInlineRowEdit({
        saveData: props.saveData,
        reload: table.loadData,
        t,
        fields: props.fields,
    });

    const getFirstEditableFieldIndex = () => {
        return columns.visibleColumns.findIndex(col => {
            const fieldName = col.name.toLowerCase();
            const field = props.fields.find(f => f.name === col.name);
            const fileType = field?.file_type;

            return fieldName !== "id" && fileType !== "string_disabled";
        });
    };

    const handleDeleteRow = async (row: TableRow) => {
        if (!canDelete) {
            ToastService.error(t("noDeletePermission"));
            return;
        }
        setRowToDelete(row);
        setIsDeleteModalOpen(true);
    };

    const confirmDeleteRow = async () => {
        if (!rowToDelete || !props.deleteData) return;

        await props.deleteData(rowToDelete);
        await table.loadData();

        ToastService.success(t("rowDeleted"));

        setIsDeleteModalOpen(false);
        setRowToDelete(null);
    };

    const updateStatuses = () => {
        if (props.reportActions) {
            const source_statuses = props.reportActions.find(action => action.type === "source_status");


            if (source_statuses) {
                for (const source of table.data) {

                    console.log('source', source);
                    postUser("report/source_status", {
                        sourceId: source.id.toString(),
                    }).then((res) => {
                        sourcesStatus[source.id] = res.result === 'yes';
                        setSourcesStatus({...sourcesStatus});
                    });
                }
            }

        }
    }

    useEffect(() => {
        const intervalId = setInterval(() => {
            updateStatuses();
        }, 5000);

        updateStatuses();

        return () => {
            clearInterval(intervalId);
        };
    }, [table.data]);

    const cancelDeleteRow = () => {
        setIsDeleteModalOpen(false);
        setRowToDelete(null);
    };

    const handleEditSelectedRow = () => {
        if (!canEdit) {
            ToastService.error(t("noEditPermission"));
            return;
        }
        const row = selection.selectedRow;
        if (!row) return;

        if (selection.selectedRowType === "data") {
            inline.startEdit(row as TableRow);
        }
    };

    const handleDeleteSelectedRow = () => {
        if (!canDelete) {
            ToastService.error(t("noDeletePermission"));
            return;
        }
        const row = selection.selectedRow;
        if (!row) return;

        if (selection.selectedRowType === "data") {
            handleDeleteRow(row as TableRow);
        } else {
            pending.setPendingRows(prev => prev.filter(r => r.id !== row.id));
        }
    };

    const handleCopySelected = () => {
        const row = selection.selectedRow;
        if (!row) return;

        if (selection.selectedRowType === "pending") {
            clipboard.copy(String(row.id));
        }
    };

    const handleAddRow = () => {
        if (!canCreate) {
            ToastService.error(t("noCreatePermission"));
            return;
        }
        pending.addNewRow();
    };

    const isAnyModalOpen =
        isFilterModalOpen ||
        isEditModalOpen ||
        isManageColumnsOpen ||
        isHelpModalOpen ||
        isDeleteModalOpen;
    const isEditing = inline.editingRowId !== null || pending.pendingRows.length > 0;

    useTableHotkeys({
        disabled: isAnyModalOpen,
        isEditing,
        focusFirstColumn: selection.focusFirstColumn, 
        onHelp: () => setIsHelpModalOpen(true),
        onAddRow: canCreate ? handleAddRow : undefined,

        onSaveAll: () => {
            if (inline.editingRowId !== null) {
                if (!canEdit) {
                    ToastService.error(t("noEditPermission"));
                    return;
                }
                inline.saveEdit();
                return;
            }

            if (pending.pendingRows.length > 0) {
                if (!canCreate) {
                    ToastService.error(t("noCreatePermission"));
                    return;
                }

                const selectedPending = selection.selectedRow;
                let rowToSave;

                if (selectedPending && selection.selectedRowType === "pending") {
                    rowToSave = pending.pendingRows.find(r => r.id === selectedPending.id);
                } else {
                    rowToSave = pending.pendingRows[pending.pendingRows.length - 1];
                }

                if (rowToSave) {
                    const rowErrors = (rowToSave as any).errors || {};
                    const hasError = Object.keys(rowErrors).length > 0;
                    const isEmpty = pending.isRowEmpty(rowToSave.data);

                    if (isEmpty) {
                        ToastService.error(t("fillRowBeforeSaving"));
                        return;
                    }

                    if (hasError) {
                        const errorMessages = Object.values(rowErrors).join(", ");
                        ToastService.error(errorMessages);
                        return;
                    }

                    pending.saveRow(String(rowToSave.id));
                }
            }
        },

        onCancelEdit: () => {
            if (inline.editingRowId !== null) {
                inline.cancelEdit();
            } else if (pending.pendingRows.length > 0) {
                const selectedPending = selection.selectedRow;
                if (selectedPending && selection.selectedRowType === "pending") {
                    pending.setPendingRows(prev => prev.filter(r => r.id !== selectedPending.id));
                } else {
                    const lastPending = pending.pendingRows[pending.pendingRows.length - 1];
                    if (lastPending) {
                        pending.setPendingRows(prev => prev.filter(r => r.id !== lastPending.id));
                    }
                }
            }
        },

        onEditRow: canEdit ? handleEditSelectedRow : undefined,
        onDeleteRow: canDelete ? handleDeleteSelectedRow : undefined,

        onToggleInlineFilter: () =>
            setActiveFilterCol(prev => {
                if (prev) return null;
                return columns.visibleColumns[0]?.name || null;
            }),
        onOpenFilterModal: () => setIsFilterModalOpen(true),
        onApplyFilter: () => table.applyFilters(activeFilters),
        onSelectNextRow: () => selection.moveSelection(1),
        onSelectPrevRow: () => selection.moveSelection(-1),
        onSelectNextColumn: () => selection.moveColumnSelection(1),
        onSelectPrevColumn: () => selection.moveColumnSelection(-1),
        onFirstPage: () => table.setCurrentPage(1),
        onLastPage: () =>
            table.setCurrentPage(Math.ceil(table.totalItems / table.rowsPerPage)),
        onPrevPage: () => table.setCurrentPage(Math.max(1, table.currentPage - 1)),
        onNextPage: () => table.setCurrentPage(table.currentPage + 1),

        onManageColumns: () => setIsManageColumnsOpen(true),
        onSortColumn: () => {
            const firstCol = columns.visibleColumns[0]?.name;
            if (firstCol) table.handleSort(firstCol);
        },
        onCopy: handleCopySelected,
        onPaste: () => {
            const row = selection.selectedRow;
            if (row && selection.selectedRowType === "pending") {
                const firstCol = columns.visibleColumns[0]?.name;
                if (firstCol) clipboard.paste(String(row.id), firstCol);
            }
        },
        onPasteValues: () => {
            const row = selection.selectedRow;
            if (row && selection.selectedRowType === "pending") {
                const firstCol = columns.visibleColumns[0]?.name;
                if (firstCol) clipboard.pasteValues(String(row.id), firstCol);
            }
        },

        onBack: () => navigate("/dashboard"),
    });

    const startResize = (e: React.MouseEvent, colName: string) => {
        const startX = e.clientX;
        const startWidth = columnWidths[colName] ?? 150;

        const onMouseMove = (moveEvent: MouseEvent) => {
            const newWidth = Math.max(80, startWidth + (moveEvent.clientX - startX));
            setColumnWidths(prev => ({ ...prev, [colName]: newWidth }));
        };

        const onMouseUp = () => {
            document.removeEventListener("mousemove", onMouseMove);
            document.removeEventListener("mouseup", onMouseUp);
        };

        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    };

    useEffect(() => {
        const saved = localStorage.getItem("table-column-widths");
        if (saved) setColumnWidths(JSON.parse(saved));
    }, []);

    useEffect(() => {
        localStorage.setItem("table-column-widths", JSON.stringify(columnWidths));
    }, [columnWidths]);

    const isRowSelected = (index: number, type: "data" | "pending") => {
        return selection.selectedRowIndex === index && selection.selectedRowType === type;
    };

    const pendingRowsWithErrors = pending.pendingRows.filter(row => {
        const errors = (row as any).errors || {};
        return Object.keys(errors).length > 0;
    });

    const totalErrorCount = pendingRowsWithErrors.length + (inline.hasErrors ? 1 : 0);

    if (!canRead) {
        return (
            <div className={styles.tableContainer}>
                <div className={styles.topBar}>
                    <div className={styles.topBarLeft}>
                        <button
                            onClick={() => navigate("/dashboard")}
                            className={styles.backButton}
                            title={t("back") || "Back"}
                        >
                            <FontAwesomeIcon icon={getIcon("arrow-left")} />
                        </button>

                        {props.module && (
                            <div className={styles.moduleInfo}>
                                <h1 className={styles.moduleTitle}>{props.module.name}</h1>
                                {props.module.description && (
                                    <p className={styles.moduleDescription}>
                                        {props.module.description}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.noAccessWrapper}>
                    {t("noReadPermission")}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.tableContainer}>
            <div className={styles.topBar}>
                <div className={styles.topBarLeft}>
                    <button
                        onClick={() => navigate("/dashboard")}
                        className={styles.backButton}
                        title={t("back")}
                    >
                        <FontAwesomeIcon icon={getIcon("arrow-left")} />
                    </button>

                    {props.module && (
                        <div className={styles.moduleInfo}>
                            <h1 className={styles.moduleTitle}>{props.module.name}</h1>
                            {props.module.description && (
                                <p className={styles.moduleDescription}>
                                    {props.module.description}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                <div className={styles.topBarRight}>
                    {!isReportMode && canCreate && (
                        <button
                            onClick={handleAddRow}
                            className={styles.advancedFilterButton}
                        >
                            <FontAwesomeIcon icon={getIcon("plus")} />
                            <span>{t("addRow")}</span>
                        </button>
                    )}

                    {props.manualSaveEnabled && canCreate && (
                        <button
                            onClick={pending.saveAllPendingRows}
                            className={styles.saveButton}
                        >
                            <FontAwesomeIcon icon={getIcon("save")} />
                            {t("saveAll")}
                        </button>
                    )}
                    <button
                        className={styles.iconButton}
                        onClick={() => setIsFilterModalOpen(true)}
                    >
                        <FontAwesomeIcon icon={getIcon("filter")} />
                        {table.filters &&
                            Array.isArray(table.filters) &&
                            table.filters.length > 0 && (
                                <span className={styles.badge}>{table.filters.length}</span>
                            )}
                    </button>

                    <button
                        className={styles.iconButton}
                        onClick={() => setIsManageColumnsOpen(true)}
                    >
                        <FontAwesomeIcon icon={getIcon("eye")} />
                    </button>

                    <button
                        className={styles.iconButton}
                        onClick={() => setIsHelpModalOpen(true)}
                    >
                        <FontAwesomeIcon icon={getIcon("question")} />
                    </button>
                </div>
            </div>

            <div className={styles.tableWrapper}>
                {table.loading && (
                    <div className={styles.loadingOverlay}>
                        <div className={styles.loader}></div>
                    </div>
                )}

                <table
                    className={styles.table}
                    style={{ minWidth: columns.visibleColumns.length * 250 + 100 }}
                >
                    <thead>
                        <tr className={styles.headerRow}>
                            {columns.visibleColumns.map(col => (
                                <th
                                    key={col.name}
                                    className={styles.headerCell}
                                    style={{ width: columnWidths[col.name] ?? 200 }}
                                >
                                    <div className={styles.headerContent}>
                                        <span className={styles.headerLabel}>{col.name}</span>
                                        <div className={styles.headerActions}>
                                            <button
                                                className={styles.sortButton}
                                                onClick={() => table.handleSort(col.name)}
                                            >
                                                <FontAwesomeIcon icon={getIcon("sort")} />
                                            </button>

                                            <button
                                                className={styles.filterButton}
                                                onClick={() => {
                                                    setActiveFilterCol(prev =>
                                                        prev === col.name ? null : col.name
                                                    );
                                                }}
                                            >
                                                <FontAwesomeIcon icon={getIcon("filter")} />
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className={styles.resizer}
                                        onMouseDown={e => startResize(e, col.name)}
                                    ></div>
                                </th>
                            ))}

                            {(canEdit || canDelete || isReportMode) && (
                                <th
                                    className={`${styles.headerCell} ${styles.actionsCell}`}
                                >
                                    {t("actions")}
                                </th>
                            )}
                        </tr>
                    </thead>

                    <tbody>
                        {activeFilterCol && (
                            <tr className={styles.filterRow}>
                                {columns.visibleColumns.map(col => (
                                    <td key={col.name} className={styles.dataCell}>
                                        {activeFilterCol === col.name &&
                                            getField({
                                                col,
                                                cellValue: activeFilters[col.name] ?? "",
                                                isEditing: true,
                                                inline: {
                                                    handleEditChange: (_, value) => {
                                                        setActiveFilters(prev => {
                                                            const next = {
                                                                ...prev,
                                                                [col.name]: value,
                                                            };
                                                            table.applyFilters(next);
                                                            return next;
                                                        });
                                                    },
                                                    handleKey: () => {},
                                                },
                                                t,
                                                props,
                                                options,
                                                setOptions,
                                                errors: inline.editErrors,
                                            })}
                                    </td>
                                ))}
                                {(canEdit || canDelete || isReportMode) && <td></td>}
                            </tr>
                        )}

                        {table.data.length === 0 &&
                            pending.pendingRows.length === 0 &&
                            !table.loading && (
                                <tr>
                                    <td
                                        colSpan={
                                            columns.visibleColumns.length +
                                            (canEdit || canDelete || isReportMode ? 1 : 0)
                                        }
                                    >
                                        <div className={styles.emptyState}>
                                            {t("noDataAvailable")}
                                        </div>
                                    </td>
                                </tr>
                            )}

                        {table.data.map((row, rowIndex) => {
                            const isRowEditing = inline.editingRowId === row.id;
                            const isSelected = isRowSelected(rowIndex, "data");

                            return (
                                <tr
                                    key={row.id}
                                    data-row-index={rowIndex}
                                    data-row-type="data"
                                    className={`
                                        ${styles.dataRow} 
                                        ${isSelected ? styles.selectedRow : ""}
                                        ${
                                            isRowEditing && inline.hasErrors
                                                ? styles.errorRow
                                                : ""
                                        }
                                        ${isRowEditing ? styles.editingRow : ""}
                                    `}
                                    onClick={() => {
                                        selection.setSelectedRowIndex(rowIndex);
                                        selection.setSelectedRowType("data");
                                    }}
                                >
                                    {columns.visibleColumns.map((col, colIndex) => {
                                        const cellValue = isRowEditing
                                            ? inline.editBuffer[col.name]
                                            : (row as any)[col.name];
                                        const fieldError = isRowEditing
                                            ? inline.editErrors[col.name]
                                            : undefined;
                                        const isFirstEditableField =
                                            colIndex === getFirstEditableFieldIndex();

                                        return (
                                            <td
                                                key={col.name}
                                                data-field-name={col.name}
                                                className={`${styles.dataCell} ${
                                                    fieldError ? styles.hasError : ""
                                                }`}
                                                data-label={col.name}
                                                onClick={(e) => {
                                                    e.stopPropagation(); 
                                                    selection.setSelectedColumnIndex(colIndex); 
                                                }}
                                            >
                                                <div className={styles.cellWrapper}>
                                                    {getField({
                                                        col,
                                                        cellValue,
                                                        isEditing:
                                                            isRowEditing && canEdit,
                                                        inline: {
                                                            ...inline,
                                                            firstFieldRef:
                                                                inline.firstFieldRef,
                                                        },
                                                        t,
                                                        props,
                                                        options,
                                                        setOptions,
                                                        isFirstField:
                                                            isFirstEditableField,
                                                        errors: inline.editErrors,
                                                    })}

                                                    {fieldError && (
                                                        <span
                                                            className={styles.errorIcon}
                                                            onMouseEnter={() =>
                                                                setHoveredError({
                                                                    rowId: row.id,
                                                                    field: col.name,
                                                                    type: "edit",
                                                                })
                                                            }
                                                            onMouseLeave={() =>
                                                                setHoveredError(null)
                                                            }
                                                        >
                                                            !
                                                            {hoveredError?.rowId ===
                                                                row.id &&
                                                                hoveredError?.field ===
                                                                    col.name &&
                                                                hoveredError?.type ===
                                                                    "edit" && (
                                                                    <span
                                                                        className={
                                                                            styles.errorTooltip
                                                                        }
                                                                    >
                                                                        {fieldError}
                                                                    </span>
                                                                )}
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        );
                                    })}

                                    {(canEdit || canDelete || isReportMode) && (
                                        <td
                                            className={`${styles.dataCell} ${styles.actionsCell}`}
                                        >
                                            {isRowEditing ? (
                                                <>
                                                    {canEdit && (
                                                        <button
                                                            onClick={
                                                                inline.saveEdit
                                                            }
                                                            className={
                                                                styles.actionBtn
                                                            }
                                                            disabled={
                                                                inline.hasErrors
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={getIcon(
                                                                    "check"
                                                                )}
                                                            />
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={
                                                            inline.cancelEdit
                                                        }
                                                        className={
                                                            styles.actionBtn
                                                        }
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={getIcon(
                                                                "xmark"
                                                            )}
                                                        />
                                                    </button>
                                                </>
                                            ) : (
                                                <>
                                                    {!isReportMode && canEdit && (
                                                        <button
                                                            onClick={() =>
                                                                inline.startEdit(
                                                                    row
                                                                )
                                                            }
                                                            className={
                                                                styles.actionBtn
                                                            }
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={getIcon(
                                                                    "pen"
                                                                )}
                                                            />
                                                        </button>
                                                    )}

                                                    {!isReportMode &&
                                                        canDelete && (
                                                            <button
                                                                onClick={() =>
                                                                    handleDeleteRow(
                                                                        row
                                                                    )
                                                                }
                                                                className={
                                                                    styles.actionBtn
                                                                }
                                                            >
                                                                <FontAwesomeIcon
                                                                    icon={getIcon(
                                                                        "trash"
                                                                    )}
                                                                />
                                                            </button>
                                                        )}

                                                    {isReportMode &&
                                                    props.reportActions
                                                        ? props.reportActions.map(
                                                              action => (
                                                                  <button
                                                                      key={
                                                                          action.icon
                                                                      }
                                                                      onClick={() =>
                                                                          props.reportActionCallBack &&
                                                                          props.reportActionCallBack(
                                                                              action,
                                                                              row
                                                                          )
                                                                      }
                                                                      className={
                                                                          styles.actionBtn + (action.type === 'source_status' ? ' ' + (
                                                                              sourcesStatus[row.id] ? styles.sourceStatusYes : styles.sourceStatusNo
                                                                          ) : '')
                                                                      }
                                                                  >
                                                                      <FontAwesomeIcon
                                                                          icon={getIcon(
                                                                              action.icon
                                                                          )}
                                                                      />
                                                                  </button>
                                                              )
                                                          )
                                                        : null}
                                                </>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}

                        {pending.pendingRows.map((pendingRow, pendingIndex) => {
                            const rowErrors = (pendingRow as any).errors || {};
                            const hasError =
                                Object.keys(rowErrors).length > 0;
                            const isSaving =
                                (pendingRow as any).isSaving || false;
                            const absoluteIndex =
                                table.data.length + pendingIndex;
                            const isSelected = isRowSelected(
                                absoluteIndex,
                                "pending"
                            );

                            return (
                                <tr
                                    key={pendingRow.id}
                                    data-row-index={absoluteIndex}
                                    data-row-type="pending"
                                    className={`
                                        ${styles.newRow}
                                        ${hasError ? styles.errorRow : ""}
                                        ${isSaving ? styles.savingRow : ""}
                                        ${isSelected ? styles.selectedRow : ""}
                                    `}
                                    onClick={() => {
                                        selection.setSelectedRowIndex(
                                            absoluteIndex
                                        );
                                        selection.setSelectedRowType(
                                            "pending"
                                        );
                                    }}
                                >
                                    {columns.visibleColumns.map(
                                        (col, colIndex) => {
                                            const fieldError =
                                                rowErrors[col.name];
                                            const cellValue =
                                                pendingRow.data[col.name];
                                            const isFirstEditableField =
                                                colIndex ===
                                                getFirstEditableFieldIndex();

                                            return (
                                                <td
                                                    key={col.name}
                                                    data-field-name={col.name}
                                                    className={`${styles.dataCell} ${
                                                        fieldError
                                                            ? styles.hasError
                                                            : ""
                                                    }`}
                                                    data-label={col.name}
                                                    onClick={(e) => {
                                                        e.stopPropagation(); 
                                                        selection.setSelectedColumnIndex(colIndex); 
                                                    }}
                                                >
                                                    <div
                                                        className={
                                                            styles.cellWrapper
                                                        }
                                                    >
                                                        {getField({
                                                            col,
                                                            cellValue,
                                                            isEditing: true,
                                                            inline: {
                                                                handleEditChange:
                                                                    (
                                                                        _,
                                                                        value
                                                                    ) =>
                                                                        pending.handleCellChange(
                                                                            pendingRow.id,
                                                                            col.name,
                                                                            value
                                                                        ),
                                                                handleKey:
                                                                    () => {},
                                                                firstFieldRef:
                                                                    pending.firstFieldRef,
                                                            },
                                                            t,
                                                            props,
                                                            options,
                                                            setOptions,
                                                            isFirstField:
                                                                isFirstEditableField,
                                                            errors: inline.editErrors,
                                                        })}

                                                        {fieldError && (
                                                            <span
                                                                className={
                                                                    styles.errorIcon
                                                                }
                                                                onMouseEnter={() =>
                                                                    setHoveredError(
                                                                        {
                                                                            rowId: String(
                                                                                pendingRow.id
                                                                            ),
                                                                            field: col.name,
                                                                            type: "pending",
                                                                        }
                                                                    )
                                                                }
                                                                onMouseLeave={() =>
                                                                    setHoveredError(
                                                                        null
                                                                    )
                                                                }
                                                            >
                                                                !
                                                                {hoveredError?.rowId ===
                                                                    String(
                                                                        pendingRow.id
                                                                    ) &&
                                                                    hoveredError?.field ===
                                                                        col.name &&
                                                                    hoveredError?.type ===
                                                                        "pending" && (
                                                                        <span
                                                                            className={
                                                                                styles.errorTooltip
                                                                            }
                                                                        >
                                                                            {
                                                                                fieldError
                                                                            }
                                                                        </span>
                                                                    )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                            );
                                        }
                                    )}

                                    <td
                                        className={`${styles.dataCell} ${styles.actionsCell}`}
                                    >
                                        {!props.autoSaveEnabled && canCreate && (
                                            <button
                                                onClick={() =>
                                                    pending.saveRow(
                                                        pendingRow.id
                                                    )
                                                }
                                                className={styles.actionBtn}
                                                disabled={
                                                    isSaving ||
                                                    hasError ||
                                                    pending.isRowEmpty(
                                                        pendingRow.data
                                                    )
                                                }
                                            >
                                                {isSaving ? (
                                                    <div
                                                        className={
                                                            styles.miniLoader
                                                        }
                                                    ></div>
                                                ) : (
                                                    <FontAwesomeIcon
                                                        icon={getIcon("check")}
                                                    />
                                                )}
                                            </button>
                                        )}

                                        <button
                                            onClick={() =>
                                                pending.setPendingRows(prev =>
                                                    prev.filter(
                                                        r =>
                                                            r.id !==
                                                            pendingRow.id
                                                    )
                                                )
                                            }
                                            className={styles.actionBtn}
                                            disabled={isSaving}
                                        >
                                            <FontAwesomeIcon
                                                icon={getIcon("xmark")}
                                            />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            <div className={styles.bottomBar}>
                <div className={styles.statistics}>
                    <span>
                        {t("total")}: {table.totalItems}
                    </span>
                    <span>
                        {t("selected")}: {selectedRows.length}
                    </span>
                    {pending.pendingRows.length > 0 && (
                        <span
                            className={`${styles.pendingBadge} ${
                                pendingRowsWithErrors.length > 0
                                    ? styles.hasErrors
                                    : ""
                            }`}
                        >
                            {t("pending")}: {pending.pendingRows.length}
                            {pendingRowsWithErrors.length > 0 &&
                                ` (${pendingRowsWithErrors.length} ${t(
                                    "rowsWithErrors"
                                )})`}
                        </span>
                    )}
                    {inline.editingRowId && inline.hasErrors && (
                        <span
                            className={
                                styles.pendingBadge + " " + styles.hasErrors
                            }
                        >
                            {t("editingRowHasErrors")}
                        </span>
                    )}
                </div>

                <div className={styles.paginationContainer}>
                    <Pagination
                        totalItems={table.totalItems}
                        itemsPerPage={table.rowsPerPage}
                        currentPage={table.currentPage}
                        onPageChange={table.setCurrentPage}
                        onItemsPerPageChange={table.setRowsPerPage}
                    />
                </div>

                {pending.pendingRows.length > 0 &&
                    props.manualSaveEnabled &&
                    canCreate && (
                        <div className={styles.bottomBarRight}>
                            <button
                                onClick={pending.saveAllPendingRows}
                                className={styles.applyButton}
                                disabled={pendingRowsWithErrors.length > 0}
                            >
                                <FontAwesomeIcon icon={getIcon("check")} />
                                {t("applyAll")}
                            </button>
                        </div>
                    )}
            </div>

            {totalErrorCount > 0 && (
                <div className={styles.validationNotice}>
                    <div className={styles.icon}>
                        <FontAwesomeIcon
                            icon={getIcon("exclamation-triangle")}
                        />
                    </div>
                    <div className={styles.text}>
                        {totalErrorCount === 1
                            ? t("validationErrorsSingle")
                            : `${t("validationErrorsMultiple")} ${totalErrorCount}`}
                    </div>
                </div>
            )}

            <FilterModal
                isOpen={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                onApply={conditions => {
                    const adapted = convertArrayToObject(conditions);
                    table.applyFilters(adapted);
                }}
                fields={props.fields}
                activeFilters={convertObjectToArray(table.filters)}
                options={options}
                setOptions={setOptions}
            />

            <EditRowModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                rowData={rowToEdit}
                columns={props.fields.map(f => f.name)}
                onSave={async params => {
                    await props.saveData?.(params);
                    await table.loadData();
                    setIsEditModalOpen(false);
                    ToastService.success(t("rowUpdated"));
                }}
            />

            <ManageColumnsVisibilityModal
                isOpen={isManageColumnsOpen}
                onClose={() => setIsManageColumnsOpen(false)}
                columns={props.fields.map(f => f.name)}
                visibleColumns={columns.visibleColumns.map(c => c.name)}
                onApply={cols =>
                    columns.setVisibleColumns(
                        props.fields.filter(f => cols.includes(f.name))
                    )
                }
            />

            <HelpModal
                isOpen={isHelpModalOpen}
                onClose={() => setIsHelpModalOpen(false)}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onConfirm={confirmDeleteRow}
                onCancel={cancelDeleteRow}
                title={t("confirmDeleteRow")}
                message={t("confirmDeleteRowMessage")}
            />
        </div>
    );
}
